import { AgencyInfo, CEJobSeries, OccupationAggregate } from "../Shared/scriptts/interfaces";
import { getStandardDeviation, htmlEncode } from "../Shared/scriptts/utilities";
import { CEResponses, initCEResponses, setCEResponses, NumberOfQuestions } from "./career-explorer-main";

declare var submittedCEResponses: number[];
declare var ceJobSeries: CEJobSeries[];
declare var occupationAggregates: Record<string, OccupationAggregate>;

const Url = new URL(location.href);
const NumberOfTopResults = 10;
const ResultsPageSize = 10;
let resultsPageIndex = 0;

document.addEventListener('DOMContentLoaded', () => {
    initResults();
    bindEvents();
});

function initResults() {
    const topResultsElem = document.querySelector<HTMLElement>('#topResults');
    const urlParts = Url.pathname.endsWith('/') ? Url.pathname.substring(0, Url.pathname.length - 1).split('/') : Url.pathname.split('/');
    const responseId = parseInt(urlParts[urlParts.length - 1]) || 0;
    if (topResultsElem === null) {
        return;
    }
    if (submittedCEResponses?.length < 1) {
        location.replace('/careerexplorer');
        return;
    }

    submittedCEResponses = getStandardizedResponses(submittedCEResponses);
    orderJobSeries();
    displayResultsSummary();
    if (CEResponses.responseId === 0 || CEResponses.responseId !== responseId) {
        CEResponses.responseId = responseId;
        CEResponses.resultsPageIndex = 0;
        CEResponses.responses = [];
        setCEResponses(CEResponses);
    }
    displayResultsDetail();
    for (let i = ResultsPageSize - 1; i < CEResponses.resultsPageIndex; i += ResultsPageSize) {
        loadNextResults();
    }
    if (CEResponses?.responses?.length === NumberOfQuestions) {
        document.querySelector<HTMLElement>('#changeResultsButton')?.classList.remove('hidden');
    }
}

function bindEvents() {
    document.querySelector<HTMLButtonElement>('#loadMoreButton')?.addEventListener('click', loadMoreButtonClicked);
    document.querySelector<HTMLElement>('#copyResultsUrl')?.addEventListener('click', copyResultsClicked);
    document.querySelector<HTMLElement>('#resultsUrl')?.addEventListener('click', copyResultsClicked);
    document.querySelector<HTMLElement>('#reTakeQuizButton')?.addEventListener('click', reTakeQuizButtonClicked);
    document.querySelector<HTMLElement>('#changeResultsButton')?.addEventListener('click', changeResultsButtonClicked);    
    bindFaqAccordionEvents();
}

function bindFaqAccordionEvents() {
    const faqSection = document.querySelector<HTMLElement>('#faqSection');
    const faqAccordion = document.querySelector<HTMLElement>('#faqAccordion');
    const faqList = document.querySelector<HTMLElement>('#faqList');
    if (faqSection === null || faqAccordion === null || faqList === null) {
        return;
    }
    const previousSibling = faqSection.previousElementSibling;
    if (previousSibling === null || previousSibling === undefined) {
        return;
    }
    const observer = new IntersectionObserver((ioEntries) => {
        ioEntries.forEach(e => {
            if (!e.isIntersecting) {
                faqAccordion.classList.remove('hidden');
                faqList.classList.add('hidden');
            }
            else {
                faqAccordion.classList.add('hidden');
                faqList.classList.remove('hidden');
            }
        })
    }, {
        root: null,
        threshold: 0.1
    });
    observer.observe(previousSibling);
}

function loadMoreButtonClicked() {
    loadNextResults();
    CEResponses.resultsPageIndex = resultsPageIndex;
    setCEResponses(CEResponses);
}

function reTakeQuizButtonClicked() {
    initCEResponses();
    location.href = '/careerexplorer/quiz';
}
function changeResultsButtonClicked() {
    CEResponses.currentQuestionIndex = -1;
    setCEResponses(CEResponses);
    location.href = '/careerexplorer/quiz';
}
function copyResultsClicked() {
    const urlElem = document.querySelector<HTMLInputElement>('#resultsUrl');
    if (urlElem === null) {
        return;
    }
    urlElem.select();
    navigator.clipboard.writeText(urlElem.value)
        .then(() => {
            const msgElem = document.querySelector<HTMLElement>('#copyResultsMsg');
            if (msgElem === null) {
                return;
            }
            msgElem.classList.add('opacity-100');
            setTimeout(() => {
                msgElem.classList.remove('opacity-100');
            }, 2000);
        })
        .catch(ex => {
            console.debug('Error copying to clipboard.', ex);
        });
}

function getStandardizedResponses(responses: number[]) {
    const k = 1e-100;
    const mean = responses.reduce((total, currentVal) => { return total + currentVal; }, 0) / responses.length;
    const std = getStandardDeviation(responses);
    return responses.map(x => ((x - mean) / (std + k)));
}

function orderJobSeries() {
    ceJobSeries.forEach(series => {
        series.correlationScore = getPearsonCorrelation(submittedCEResponses, series.questionValues);
    });
    ceJobSeries.sort((a, b) => b.correlationScore - a.correlationScore);
}

function loadNextResults() {
    resultsPageIndex += ResultsPageSize;
    displayResultsDetail();
}

function displayResultsSummary() {
    const ulElem = document.querySelector<HTMLElement>('#topResults');
    if (ulElem === null) {
        return;
    }
    for (let i = 0; i < NumberOfTopResults; ++i) {
        if (i >= ceJobSeries.length) {
            break;
        }
        const liElem = document.createElement('li');
        const aElem = document.createElement('a');
        aElem.href = `#${ceJobSeries[i]?.seriesCode}`;
        aElem.innerHTML = `${ceJobSeries[i]?.seriesName} (series ${ceJobSeries[i]?.seriesCode})`;
        aElem.className = 'no-underline font-bold text-white hover:text-white hover:underline visited:text-white';
        liElem.appendChild(aElem);
        ulElem.appendChild(liElem);
    }
}

function displayResultsDetail() {
    const detailsContainer = document.querySelector<HTMLElement>('#resultDetails');
    if (detailsContainer === null) {
        return;
    }
    const length = ceJobSeries.length < (resultsPageIndex + ResultsPageSize) ? ceJobSeries.length : (resultsPageIndex + ResultsPageSize);
    for (let i = resultsPageIndex; i < length; ++i) {
        const series = ceJobSeries[i];
        if (series === null) {
            break;
        }
        let seriesContent = `
        <article id="${series.seriesCode}" class="w-full scroll-mt-[15rem] md:scroll-mt-[10rem]${i % 2 == 0 ? "" : " bg-gray-lightest"}">
        <div class="md:max-w-3xl mx-auto p-4 mt-4">
        <h2>${series.seriesName} (series ${series.seriesCode})</h2>
        <div class="bg-primary-lighter p-4">Based on your answers, your interests are a ${getResultMatchHtml(series)} with this job series.</div>
        <p>${series.description.replace('<ul>', '<ul class="list-disc ml-6">')}</p>`;
        let relatedJobsContent = '';
        const relatedJobs = series.relatedJobTitles.filter(j => j !== '');
        if (relatedJobs.length > 0) {
            relatedJobsContent += '<h3>Popular job titles</h3><ul class="list-disc ml-6">';
            relatedJobs.forEach(job => {
                if (job === null || job.trim() === '') {
                    return;
                }
                relatedJobsContent += `<li>${job.trim()}</li>`
            });
            relatedJobsContent += '</ul>';
        }
        const openJobsContent = getOpenJobsContent(series.seriesCode);
        let searchButton = `
            <div class="mt-4 flex justify-end">
                <a href="/Search?j=${series.seriesCode}" class="button button-primary w-full sm:w-fit flex items-center">
                    <svg class="fill-white p-0 w-5 h-5 mr-1" aria-hidden="true" role="presentation"><use xlink:href="/img/icons/sprite.svg#search"></use></svg> See open jobs in this series
                </a>
            </div>
        `;
        detailsContainer.innerHTML += seriesContent + relatedJobsContent + openJobsContent + searchButton + '</div></article>';
    }
    updateLoadMoreButtonState();
}

function getOpenJobsContent(seriesCode: string): string {
    const aggregate = occupationAggregates[seriesCode];
    if (!aggregate || aggregate.positionCount === 0) {
        return '';
    }

    return `<p>There are ${aggregate.positionCount.toLocaleString()} open jobs at ${getSearchLinks(seriesCode, aggregate) }.</p>`;
}

function getSearchLinks(seriesCode: string, aggregate: OccupationAggregate): string {
    const maxAgencies = 3;
    const agencies = aggregate.agencies.slice(0, maxAgencies);
    const additionalCount = aggregate.totalAgencyCount - agencies.length;
    const searchLink = (agency: AgencyInfo) => `<a href="/search/?j=${seriesCode}&a=${encodeURIComponent(agency.code)}">${htmlEncode(agency.name)}</a>`;
    const searchLinks = agencies.map(searchLink);

    if (additionalCount > 0) {
        return `${searchLinks.join(', ')} and ${additionalCount} more`;
    }

    return searchLinks.join(', ');
}

function getResultMatchHtml(result: CEJobSeries) {
    let matchValue = 'weak',
        matchClass = 'text-emergency';
    if (result.correlationScore >= 0.47) {
        matchValue = 'very strong';
        matchClass = 'text-green-vivid-5';
    }
    else if (result.correlationScore >= 0.37) {
        matchValue = 'strong';
        matchClass = 'text-green-vivid-70';
    }
    else if (result.correlationScore >= 0.19) {
        matchValue = 'moderate';
        matchClass = 'text-accent-warm-darker';
    }
    return `<strong class="${matchClass}">${matchValue} match</strong>`;
}

function updateLoadMoreButtonState() {
    const loadMoreButton = document.querySelector<HTMLButtonElement>('#loadMoreButton');
    if (loadMoreButton === null) {
        return;
    }
    if (loadMoreButton.classList.contains('hidden')) {
        return;
    }
    if (resultsPageIndex + ResultsPageSize > ceJobSeries.length - 1) {
        loadMoreButton.classList.add('hidden');
    }
}

function getPearsonCorrelation(x: number[], y: number[]): number {
    let sumX = 0,
        sumY = 0,
        sumXY = 0,
        sumX2 = 0,
        sumY2 = 0,
        corr = 0;
    const minLength = x.length = y.length = Math.min(x.length, y.length),
        reduce = (xi: number, idx: number) => {
            const yi = y[idx];
            sumX += xi;
            sumY += yi;
            sumXY += xi * yi;
            sumX2 += xi * xi;
            sumY2 += yi * yi;
        }
    x.forEach(reduce);
    try {
        corr = (minLength * sumXY - sumX * sumY) / Math.sqrt((minLength * sumX2 - sumX * sumX) * (minLength * sumY2 - sumY * sumY));
    } catch {
        location.href = '/Error';
    } finally {
        return corr;
    }
}
